import React, { createContext, useContext, useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { invalidateOrgScopedQueries } from '../../../shared/api/cacheInvalidation';
import { useProfile } from '../hooks';

// Not authentication. The Auth0 session (identity) is the same across all
// contexts. This only controls which `X-Active-Org-Id` header goes out with
// API calls, which in turn drives `Current.organization` on the backend.
export type ActiveContext =
  | { kind: 'personal' }
  | { kind: 'org'; orgId: number; name: string; slug: string; role: string };

// Platform-role view for users who are BOTH a system admin and an instructor.
// Unlike ActiveContext (which scopes the org header), this only decides which
// platform section the sidebar surfaces; it doesn't change identity or headers.
export type PlatformView = 'admin' | 'instructor';

interface Ctx {
  active: ActiveContext;
  available: ActiveContext[];
  switchTo: (ctx: ActiveContext) => void;
  platformView: PlatformView | null;
  setPlatformView: (view: PlatformView) => void;
}

const STORAGE_KEY = 'wz.activeContext';
const PLATFORM_VIEW_KEY = 'wz.platformView';
const ActiveOrgContext = createContext<Ctx | null>(null);

// The stored context also says whose it is and whether they picked it. One
// browser holds it for everyone who uses it (an admin viewing as a user, a
// shared machine), and a "Personal" the app fell back to on its own, as when a
// membership is removed, must not keep an org admin out of their organization
// for good (Frank Benesz at Bloomingdale's, 28 Oct 2026).
type StoredContext = ActiveContext & { userId?: number; picked?: boolean };

function readStored(): StoredContext | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) as StoredContext : null;
  } catch {
    return null;
  }
}

function writeStored(ctx: ActiveContext, userId: number | undefined, picked: boolean) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...ctx, userId, picked }));
  } catch {
    // localStorage can throw in private mode; runtime state is still correct.
  }
}

function withoutMeta(stored: StoredContext): ActiveContext {
  if (stored.kind === 'personal') return { kind: 'personal' };
  const { orgId, name, slug, role } = stored;
  return { kind: 'org', orgId, name, slug, role };
}

const sameContext = (a: ActiveContext, b: ActiveContext) =>
  a.kind === 'personal' ? b.kind === 'personal' : b.kind === 'org' && a.orgId === b.orgId;

export function headerForActive(ctx: ActiveContext | null | undefined): string | null {
  if (!ctx) return null;
  return ctx.kind === 'personal' ? 'personal' : String(ctx.orgId);
}

export function readActiveContextFromStorage(): ActiveContext | null {
  return readStored();
}

export function ActiveOrgProvider({ children }: { children: React.ReactNode }) {
  const { data: profile } = useProfile();
  const queryClient = useQueryClient();

  const available = useMemo<ActiveContext[]>(() => {
    const personal: ActiveContext = { kind: 'personal' };
    const orgs = (profile?.memberships ?? []).map((m) => ({
      kind: 'org' as const,
      orgId: m.organization.id,
      name: m.organization.name,
      slug: m.organization.slug,
      role: m.role,
    }));
    return [personal, ...orgs];
  }, [profile]);

  const [active, setActive] = useState<ActiveContext>(() => {
    const stored = readStored();
    return stored ? withoutMeta(stored) : { kind: 'personal' };
  });

  const [platformView, setPlatformViewState] = useState<PlatformView | null>(() => {
    try {
      const raw = localStorage.getItem(PLATFORM_VIEW_KEY);
      return raw === 'admin' || raw === 'instructor' ? raw : null;
    } catch {
      return null;
    }
  });

  const setPlatformView = useCallback((view: PlatformView) => {
    setPlatformViewState(view);
    try {
      localStorage.setItem(PLATFORM_VIEW_KEY, view);
    } catch {
      // localStorage can throw in private mode; runtime state is still correct.
    }
  }, []);

  // Admins and owners land in their org (the admin view) unless they picked
  // "Personal" themselves. An org they're no longer in falls back the same way.
  useEffect(() => {
    if (!profile) return;
    const stored = readStored();
    const mine = stored?.userId === profile.id ? stored : null;
    const isMember = active.kind === 'personal'
      || available.some((c) => c.kind === 'org' && c.orgId === active.orgId);
    if (active.kind === 'org' && isMember) {
      if (!mine) writeStored(active, profile.id, false);
      return;
    }
    if (active.kind === 'personal' && mine?.picked) return;

    const adminOrg = available.find(
      (c) => c.kind === 'org' && (c.role === 'admin' || c.role === 'owner'),
    );
    const next = adminOrg ?? { kind: 'personal' as const };
    if (!sameContext(next, active)) setActive(next);
    if (!mine || !sameContext(next, withoutMeta(mine))) writeStored(next, profile.id, false);
  }, [profile, active, available]);

  const switchTo = useCallback(
    (ctx: ActiveContext) => {
      setActive(ctx);
      writeStored(ctx, profile?.id, true);
      invalidateOrgScopedQueries(queryClient);
    },
    [queryClient, profile?.id],
  );

  const value = useMemo<Ctx>(
    () => ({ active, available, switchTo, platformView, setPlatformView }),
    [active, available, switchTo, platformView, setPlatformView],
  );
  return <ActiveOrgContext.Provider value={value}>{children}</ActiveOrgContext.Provider>;
}

export function useActiveContext() {
  const ctx = useContext(ActiveOrgContext);
  if (!ctx) throw new Error('useActiveContext must be used inside <ActiveOrgProvider>');
  return ctx;
}
